import type { HistogramNumberRangeGlobalFilterItem } from '@weberus/shared-types';
import { hasItems } from '@/app/_shared/utils/has-items';
import { numberFormat } from '@/app/_shared/utils/number-utils';

const MAX_BAR_HEIGHT = 70; // 막대의 최대 높이 (px)
const MIN_BAR_HEIGHT = 4; // 막대의 최소 높이 (px)
const BAR_HEIGHT_RANGE = MAX_BAR_HEIGHT - MIN_BAR_HEIGHT; // 막대 높이의 범위

/**
 * 막대의 translateY 값을 계산합니다. (백분율)
 * @param count - 막대의 높이를 계산할 데이터 값 (=max와 min 사이의 값)
 * @param max - 데이터 값의 최대값
 * @param min - 데이터 값의 최소값
 * @returns 계산된 높이 백분율
 */
export const getTranslateYPercentOfBar = (count: number, max: number, min: number): number => {
  // relativePosition은 주어진 count가 전체 범위에서 차지하는 위치를 비율로 나타냅니다.
  // 0에 가까우면 범위의 최소값에 가깝고, 1에 가까우면 최대값에 가깝습니다. (0 <= relativePosition <= 1) 즉, 0~1 사이로 정규화
  const relativePosition = (count - min) / (max - min);

  // BAR_HEIGHT_RANGE를 기준으로 relativePosition에 따라 높이를 조정합니다.
  // 높이는 최소값(0)에서 최대값(BAR_HEIGHT_RANGE) 사이로 변환됩니다.
  const adjustedHeight = BAR_HEIGHT_RANGE * (1 - relativePosition);

  // heightPercentage는 adjustedHeight를 MAX_BAR_HEIGHT로 나누어 백분율로 변환합니다.
  // 이 백분율 값은 실제 막대가 차지해야 하는 높이를 결정하는데 사용됩니다.
  const heightPercentage = (adjustedHeight / MAX_BAR_HEIGHT) * 100;

  return heightPercentage;
};

/** 100의 자리에서 내림하는 함수 */
export const roundDownToHundred = (num: number): number => Math.floor(num / 100) * 100;

/**
 * 입력 필드의 값을 계산하는 함수
 * @param positionPercent - 핸들의 위치 (0 ~ 100)
 * @param min - range의 최소값
 * @param max - range의 최대값
 */
export const calculateInputValue = (min: number, max: number, positionPercent: number): string => {
  // positionPercent로 range 범위 사이의 input 값 계산 (min <= val <= max)
  const val = min + (max - min) * (positionPercent / 100);
  return numberFormat(roundDownToHundred(val));
};

/**
 * 빈을 이용하여 입력 필드의 값을 계산하는 함수
 * @param bins - 각 막대 클러스터 정보
 * @param positionPercent - 핸들의 위치 (0 ~ 100)
 */
export const calculateInputValueByBins = (bins: { from: number; to: number }[], positionPercent: number): string => {
  const sectionCount = bins.length;
  const integerPart = Math.floor((positionPercent / 100) * sectionCount); // 정수부 (0 ~ sectionCount)
  const decimalPart = (positionPercent / 100) * sectionCount - integerPart; // 소수부 (0 ~ 1)

  const isRangeAvailable = integerPart < sectionCount;

  const currentBin = isRangeAvailable
    ? bins[integerPart]
    : { from: bins[sectionCount - 1].to, to: bins[sectionCount - 1].to };

  const currentVal = currentBin.from + (currentBin.to - currentBin.from) * decimalPart;
  return numberFormat(roundDownToHundred(currentVal));
};

/**
 * 핸들의 위치를 백분율 (0 ~ 100)로 계산히는 함수
 * @param value - 핸들의 현재 값(가격)
 * @param range - range 객체
 */
const calculateHandlePosition = (
  price: number,
  range: {
    min: number;
    max: number;
  },
): number => {
  const res = ((price - range.min) / (range.max - range.min)) * 100;
  return Math.min(100, Math.max(0, res));
};

/**
 * 빈을 이용하여 위치 비율 (0~100) 을 계산하는 함수
 * @param bins - 각 막대 클러스터 정보
 * @param value - 현재 값
 * @returns 위치 비율 (0~100)
 */
const calculatePositionRateByBins = (bins: { from: number; to: number }[], value: number): number => {
  const sectionCount = bins.length;
  const rateOfBin = 100 / sectionCount;

  // 현재 값이 속한 구간
  const binIdx = bins.findIndex((bin) => value >= bin.from && value <= bin.to);
  if (binIdx === -1) {
    // url 조작 등으로 정상적이지 않은 값이 들어온 경우
    const min = bins[0].from;
    if (value < min) return 0;
    return 100;
  }
  const currentBin = bins[binIdx];

  // 현재 값이 속한 구간의 상세 위치 비율
  const detailCorrectionRatio = (value - currentBin.from) / (currentBin.to - currentBin.from);
  const positionRate = rateOfBin * binIdx + rateOfBin * detailCorrectionRatio;

  return positionRate;
};

/**
 * 위치 비율 (0~100)을 계산하는 함수
 * bins가 존재하는 경우, calculatePositionRateByBins 함수를 사용하여 계산
 * bins가 존재하지 않을 경우, calculateHandlePosition 함수를 사용하여 계산
 * @param bins - 각 막대 클러스터 정보
 * @param value - 현재 값
 * @param range - range 객체
 * @returns 위치 비율 (0~100)
 */
export const calculatePosition = ({
  bins,
  value,
  range,
}: {
  bins: HistogramNumberRangeGlobalFilterItem['bins'];
  value: number;
  range: HistogramNumberRangeGlobalFilterItem['range'];
}): number => (hasItems(bins) ? calculatePositionRateByBins(bins, value) : calculateHandlePosition(value, range));
